import { Injectable } from '@angular/core';
import { DatasetDynamicLoaderService } from 'app/registries/dataset/dataset-dynamic-loader.service';
import { LinkedServiceDynamicLoaderService } from 'app/registries/linked-service/linked-service-dynamic-loader.service';
import { ILinkedServiceTypeMetadata, ILocationPattern } from '../extensibility/metadatas/resource-type/linked-service-type-metadata';
import { IRegisteredDatasetTypeMetadata } from '../extensibility/metadatas/resource-type/dataset-type-metadata';
import { ControlMetadata } from '../extensibility/metadatas/control/control-metadata';
import { ISwitchCase, ISelectSwitchMetadata } from '../extensibility/metadatas/control/select-switch-metadata';

import { LoggerService } from 'app/logger/logger.service';
import { GlobalVariables } from './globals';
import { format, stringIsNullOrEmpty, convertObjectToString, replaceSpecialChars } from 'app/shared/utils/string.util';
import { DynamicDataType, UIControlType, mapBackendDatatypeToDynamicType, advancedControlTypes, staticTabularConnectorControlTypes, staticFileTypeConnectorControlTypes } from './datatype-to-expressiontype';
import _ from 'lodash';
import { formatTypeDatasetType, formatTypeDatasetTypes } from 'app/registries/consts/adf-model-type-map-const';
import { isStaticFormatDataset } from 'app/shared/utils/dataset-validation.util';
import { PluginMetadataApiService, IPluginMetadataSucceededResult, IPluginSettingsProperty, IUIControlsItem, IAdfAppModelConfigurationItem } from 'app/apis/plugin-metadata-api.service';
import { azureResourceSelectMethodOptions } from 'app/registries/consts/metadata-consts';
import { allExistingDynamicConnectorConfiguration, IIconRegistery, dynamicConnectorIconRegistery } from 'app/registries/consts/dynamic-connector-configuration-const';
import { getSupportedFormatCopySourceSinkType, arrayIsNullOrEmpty } from 'app/registries/utls/util';
import { IConnectorConfiguration, IConnectorProperty } from './dynamic-connector.service.models';
import customConnectorsPromise from '../../Resources/CustomConnectors/customerConnectors.definition.json';
import { filter, lastValueFrom, take } from 'rxjs';
import {
    IDataflowTypeMetadata,
    IDataflowTypeMetadataSource
} from 'app/extensibility/metadatas/resource-type/dataflow-format-type-metadata';
import { DataflowFormatDynamicLoaderService } from 'app/registries/dataflow/dataflow-format-dynamic-loader.service';
import { PluginMetadataProcessService } from 'app/services/plugin-metadata-process.service';
import {
    IDataflowSchemaTableSelectionMetadata
} from 'app/extensibility/metadatas/control/dataflow-schema-table-selection-metadata';
import { IPatternMetadata } from 'app/extensibility/metadatas/validator/pattern-metadata';
import { localStorageGet } from 'app/shared/utils/window.util';
import DataWorldConnector from 'Resources/CustomConnectors/dataWorldConnector.definition';
import AsanaConnector from 'Resources/CustomConnectors/asanaConnector.definition';
import TwilioConnector from 'Resources/CustomConnectors/twilioConnector.definition';
import AppFiguresConnector from 'Resources/CustomConnectors/appFiguresConnector.definition';
import KafkaConnector from 'Resources/CustomConnectors/kafkaConnector.definition';
import GoogleSheetsConnector from 'Resources/CustomConnectors/googleSheetsConnector.definition';

type DynamicControlMetadata = ControlMetadata & { id?: string, isSubControl?: boolean };

type UIModelConceptType = 'linkedService' | 'dataset' | 'copySource' | 'copySink' | 'location' | 'storeReadSettings' | 'storeWriteSettings' | 'dataflowInlineSource' | 'dataflowInlineSink';

type BackendModelConceptType = 'connectionSettings' | 'readSettings' | 'writeSettings' | 'readSettings or writeSettings';

interface ISchemaTableInfo {
    isTableNameDisabled?: boolean;
    isTableNameRequired?: boolean;
    tableNameAlias?: string;
    splitTablePath?: boolean;
    schemaAlias?: string;
    tableAlias?: string;
    tableNameDisplay?: string;
}

interface IQueryInfo {
    notSupportCopySourceQuery?: boolean;
    queryAlias?: string;
    queryDisplayName?: string;
    isQueryRequired?: boolean;
}

interface ILocationInfo {
    locationType?: string;
    isNonFilePathBasedLocation?: boolean;
    nonFilePathBasedLocationControls?: ControlMetadata[];
    locationProperties?: IConnectorProperty[];
    locationPattern?: ILocationPattern;
    fileNameAlias?: string;
    locationDisplayName?: string;
}

interface IPropertySettingsOfModelConcept {
    linkedService: IPluginSettingsProperty[];
    dataset?: IPluginSettingsProperty[];
    copySource?: IPluginSettingsProperty[];
    copySink?: IPluginSettingsProperty[];
    location?: IPluginSettingsProperty[];
    storeReadSettings?: IPluginSettingsProperty[];
    storeWriteSettings?: IPluginSettingsProperty[];
    dataflowInlineSource?: IPluginSettingsProperty[];
    dataflowInlineSink?: IPluginSettingsProperty[];
}

enum ConnectorType {
    INTERNAL,
    EXTERNAL
}

@Injectable({
    providedIn: 'root'
})
export class DynamicConnectorService {
    public static className = 'DynamicConnectorService';

    private _connectorTypeMap: Map<string, ConnectorType> = new Map<string, ConnectorType>();

    constructor(
        private readonly _datasetDynamicLoaderService: DatasetDynamicLoaderService,
        private readonly _loggerService: LoggerService,
        private readonly _pluginMetadataApiService: PluginMetadataApiService,
        private readonly _linkedServiceDynamicLoaderService: LinkedServiceDynamicLoaderService,
        private readonly _dataflowFormatDynamicLoaderService: DataflowFormatDynamicLoaderService,
        private readonly _pluginMetadataProcessService: PluginMetadataProcessService
    ) {
    }

    public static populateAttributesFromDynamicControl(control: ControlMetadata, attributesNames?: Set<string>): Set<string> {
        if (attributesNames == null) {
            attributesNames = new Set();
        }
        if (control.type === 'parameterizable' || control.type === 'parameterizable-dropdown') {
            attributesNames.add(control.child.bind as string);
        } else if (typeof control.bind === 'string') {
            attributesNames.add(control.bind);
        }

        if (control.cases != null && Array.isArray(control.cases)) {
            attributesNames.add(control.bind as string);
            control.cases
                .filter((c) => c.children != null && Array.isArray(c.children))
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                .forEach((c: ISwitchCase<any>) => {
                    c.children.forEach((child) => this.populateAttributesFromDynamicControl(child, attributesNames));
                });
        }
        return attributesNames;
    }

    public async addDynamicModelAsync(): Promise<void> {
        let pluginMetadatas: IPluginMetadataSucceededResult[] = [];
        // load from backend service or debug shir
        try {
            if (
                GlobalVariables.enableLoadingDynamicPluginFromService ||
                !stringIsNullOrEmpty(GlobalVariables.shirForDynamicPluginDebug)
            ) {
                pluginMetadatas = await this._pluginMetadataApiService.getDynamicLayoutMetadataAsync(
                    GlobalVariables.shirForDynamicPluginDebug
                );
                pluginMetadatas.forEach((connector) =>
                    this._connectorTypeMap.set(connector.connectorName, ConnectorType.INTERNAL)
                );
            }
        } catch (error) {
            const errorMessage = format(
                stringResources.ConnectorMetadata_LogMessageTemplate,
                `Error happens when loading dynamic connectors from ${
                    !stringIsNullOrEmpty(GlobalVariables.shirForDynamicPluginDebug) ? 'SHIR' : 'IRMS'
                }: ${convertObjectToString(error, false)}`
            );
            this._loggerService.error(DynamicConnectorService.className, errorMessage);
        }
        // load from local storage
        try {
            if (
                GlobalVariables.enableLoadingDynamicPluginFromLocalStorage &&
                localStorageGet('pluginMetadata') != null
            ) {
                const debugConfiguration = JSON.parse(localStorageGet('pluginMetadata'));
                (debugConfiguration as IPluginMetadataSucceededResult[]).forEach((connector) =>
                    this._connectorTypeMap.set(connector.connectorName, ConnectorType.EXTERNAL)
                );
                pluginMetadatas = [...pluginMetadatas, ...debugConfiguration];
            }
        } catch (error) {
            const errorMessage = format(
                stringResources.ConnectorMetadata_LogMessageTemplate,
                `Error happens when loading dynamic connectors from Local Storage: ${convertObjectToString(
                    error,
                    false
                )}`
            );
            this._loggerService.error(DynamicConnectorService.className, errorMessage);
        }

        // concat all existing dynamic configuration, fill in this array when there are any dynamic plugins need to be loaded without API call.
        if (GlobalVariables.enableLoadingDynamicPluginForDev) {
            allExistingDynamicConnectorConfiguration.forEach((connector) =>
                this._connectorTypeMap.set(connector.connectorName, ConnectorType.INTERNAL)
            );
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            pluginMetadatas = [...pluginMetadatas, ...(allExistingDynamicConnectorConfiguration as any)];
        }
        if (GlobalVariables.enableFirstClassConnector) {
            const dynamicConnectorDefinitions: IPluginMetadataSucceededResult[] = customConnectorsPromise;
            dynamicConnectorDefinitions.forEach((connector) =>
                this._connectorTypeMap.set(connector.connectorName, ConnectorType.INTERNAL)
            );
            pluginMetadatas = [...pluginMetadatas, ...(dynamicConnectorDefinitions || [])];
        }

        // load from adf resource
        try {
            const connectors: IPluginMetadataSucceededResult[] = [];
            connectors.push(DataWorldConnector);
            connectors.push(AsanaConnector);
            connectors.push(TwilioConnector);
            connectors.push(AppFiguresConnector);
            connectors.push(GoogleSheetsConnector);
            if (GlobalVariables.enableKafka) {
                connectors.push(KafkaConnector);
            }
            connectors.forEach((connector) =>
                this._connectorTypeMap.set(connector.connectorName, ConnectorType.INTERNAL)
            );
            pluginMetadatas.push(...connectors);
        } catch (error) {
            const errorMessage = format(
                stringResources.ConnectorMetadata_LogMessageTemplate,
                `Error happens when loading dynamic connectors from adf resource: ${convertObjectToString(
                    error,
                    false
                )}`
            );
            this._loggerService.error(DynamicConnectorService.className, errorMessage);
        }

        this._pluginMetadataProcessService.preProcessConfigurations(pluginMetadatas);
        await this.registerDynamicConnectorsAsync(pluginMetadatas);
    }

    public convertPluginMetadataToConnectorConfiguration(pluginMetadatas: IPluginMetadataSucceededResult[], onlyHandleLinkedservice?: boolean): IConnectorConfiguration[] {
        const result: IConnectorConfiguration[] = [];
        if (!arrayIsNullOrEmpty(pluginMetadatas)) {
            pluginMetadatas.forEach((metadataItem: IPluginMetadataSucceededResult) => {
                try {
                    // validate plugin metadata
                    const validationResult = this.validatePluginMetadataResult(metadataItem);
                    if (validationResult != null) {
                        throw new Error(validationResult);
                    }
                    this._loggerService.log(DynamicConnectorService.className,
                        format(stringResources.ConnectorMetadata_LogMessageTemplate, `Start registering connector: ${metadataItem.connectorName}`));
                    const propertySettingsOfModelConcept = this._getPropertySettingsOfModelConcept(metadataItem, onlyHandleLinkedservice);
                    const resultItem: IConnectorConfiguration = {
                        connectorName: metadataItem.connectorName,
                        icon: metadataItem.adfAppModelConfiguration.icon,
                        displayName: !stringIsNullOrEmpty(metadataItem.adfAppModelConfiguration.displayName) ?
                            metadataItem.adfAppModelConfiguration.displayName : metadataItem.connectorName,
                        dataType: metadataItem.dataType, // consistent with backend
                        category: metadataItem.adfAppModelConfiguration != null ? metadataItem.adfAppModelConfiguration.category || [] : [],
                        supportCopySource: !!(metadataItem.adfAppModelConfiguration && (metadataItem.adfAppModelConfiguration.copySource || metadataItem.adfAppModelConfiguration.storeReadSettings)),
                        supportCopySink: !!(metadataItem.adfAppModelConfiguration && (metadataItem.adfAppModelConfiguration.copySink || metadataItem.adfAppModelConfiguration.storeWriteSettings)),
                        supportEncryption: metadataItem.SupportEncryption
                    };
                    resultItem.propertyDefinition = {
                        linkedService: {
                            type: metadataItem.connectorName,
                            typeProperties: this._generateConnectorConfigsFromPluginConfigs(metadataItem, propertySettingsOfModelConcept, 'linkedService')
                        }
                    };
                    if (metadataItem.adfAppModelConfiguration.dataflowInlineSource?.itemSettings?.dataflowType) {
                        resultItem.propertyDefinition.dataflowInlineSource = {
                            type: metadataItem.adfAppModelConfiguration.dataflowInlineSource.itemSettings.dataflowType,
                            format: metadataItem.adfAppModelConfiguration.dataflowInlineSource.itemSettings?.dataflowFormat,
                            typeProperties: this._generateConnectorConfigsFromPluginConfigs(metadataItem, propertySettingsOfModelConcept, 'dataflowInlineSource'),
                            useInlineProjection: metadataItem.adfAppModelConfiguration?.dataflowInlineSource?.itemSettings?.dataflowUseInlineProjection,
                            connectTransformationProperties: metadataItem.adfAppModelConfiguration?.dataflowInlineSource?.itemSettings?.dataflowConnectTransformationProperties,
                        };
                    }
                    if (metadataItem.adfAppModelConfiguration.dataflowInlineSink?.itemSettings?.dataflowType) {
                        resultItem.propertyDefinition.dataflowInlineSink = {
                            type: metadataItem.adfAppModelConfiguration.dataflowInlineSink.itemSettings.dataflowType,
                            format: metadataItem.adfAppModelConfiguration.dataflowInlineSink.itemSettings?.dataflowFormat,
                            typeProperties: this._generateConnectorConfigsFromPluginConfigs(metadataItem, propertySettingsOfModelConcept, 'dataflowInlineSink')
                        };
                    }
                    const hasDataSetConfig: boolean = !!(metadataItem.adfAppModelConfiguration?.dataset || metadataItem.adfAppModelConfiguration?.location);
                    if (!onlyHandleLinkedservice && hasDataSetConfig) {
                        if (metadataItem.dataType === 'Binary') {
                            resultItem.propertyDefinition.location = {
                                type: `${replaceSpecialChars(metadataItem.connectorName)}Location`,
                                typeProperties: this._generateConnectorConfigsFromPluginConfigs(metadataItem, propertySettingsOfModelConcept, 'location')
                            };
                            if (metadataItem.readSettings != null) {
                                resultItem.propertyDefinition.storeReadSettings = {
                                    type: `${replaceSpecialChars(metadataItem.connectorName)}StoreReadSettings`,
                                    typeProperties: this._generateConnectorConfigsFromPluginConfigs(metadataItem, propertySettingsOfModelConcept, 'storeReadSettings')
                                };
                            }
                            if (metadataItem.writeSettings != null) {
                                resultItem.propertyDefinition.storeWriteSettings = {
                                    type: `${replaceSpecialChars(metadataItem.connectorName)}StoreWriteSettings`,
                                    typeProperties: this._generateConnectorConfigsFromPluginConfigs(metadataItem, propertySettingsOfModelConcept, 'storeWriteSettings')
                                };
                            }
                        } else {
                            resultItem.propertyDefinition.dataset = {
                                type: `${replaceSpecialChars(metadataItem.connectorName)}Dataset`,
                                typeProperties: this._generateConnectorConfigsFromPluginConfigs(metadataItem, propertySettingsOfModelConcept, 'dataset'),
                                notSupportSchema: metadataItem.adfAppModelConfiguration?.dataset?.itemSettings?.notSupportSchema,
                                notSupportImportSchema: metadataItem.adfAppModelConfiguration?.dataset?.itemSettings?.notSupportImportSchema,
                                notSupportPreviewData: metadataItem.adfAppModelConfiguration?.dataset?.itemSettings?.notSupportPreviewData
                            };
                            if (metadataItem.readSettings != null) {
                                resultItem.propertyDefinition.copySource = {
                                    type: `${replaceSpecialChars(metadataItem.connectorName)}CopySource`,
                                    typeProperties: this._generateConnectorConfigsFromPluginConfigs(metadataItem, propertySettingsOfModelConcept, 'copySource')
                                };
                            }
                            if (metadataItem.writeSettings != null) {
                                resultItem.propertyDefinition.copySink = {
                                    type: `${replaceSpecialChars(metadataItem.connectorName)}CopySink`,
                                    typeProperties: this._generateConnectorConfigsFromPluginConfigs(metadataItem, propertySettingsOfModelConcept, 'copySink')
                                };
                            }
                        }
                    }
                    result.push(resultItem);
                    this._loggerService.log(DynamicConnectorService.className,
                        format(stringResources.ConnectorMetadata_LogMessageTemplate, `Finished registering connector: ${metadataItem.connectorName}`));
                } catch (error) {
                    const errorMessage = format(stringResources.ConnectorMetadata_LogMessageTemplate, convertObjectToString(error, false));
                    this._loggerService.error(DynamicConnectorService.className, errorMessage);
                }
            });
        }
        return result;
    }

    public async registerDynamicConnectorsAsync(pluginMetadatas: IPluginMetadataSucceededResult[], onlyHandleLinkedservice?: boolean): Promise<void> {
        if (arrayIsNullOrEmpty(pluginMetadatas)) {
            return;
        }

        // convert plugin metadata to connector configuration
        const configurations = this.convertPluginMetadataToConnectorConfiguration(pluginMetadatas, onlyHandleLinkedservice);

        // register models
        const linkedSerivceMetaDatas: ILinkedServiceTypeMetadata[] = [];
        const datasetMetadDatas: IRegisteredDatasetTypeMetadata[] = [];
        configurations.forEach((c) => {
            if (c.propertyDefinition == null) {
                this._loggerService.log(DynamicConnectorService.className, format(stringResources.ConnectorMetadata_LogMessageTemplate,
                    `Invalid configuration: no propertyDefinition for configuration ${c.connectorName}`));
                return;
            }
            try {
                const dataKind = convertDataStructureToDataKind(c.dataType);

                const tableNameInfo: ISchemaTableInfo = ((c.dataType === 'Tabular' || c.dataType === 'Hierarchical') && c.propertyDefinition.dataset)
                    ? getTableNameInfo(c.propertyDefinition.dataset.typeProperties) : undefined;
                const queryInfo: IQueryInfo = ((c.dataType === 'Tabular' || c.dataType === 'Hierarchical') && c.propertyDefinition.copySource) ?
                    getQueryInfo(c.propertyDefinition.copySource.typeProperties) : undefined;
                const locationInfo: ILocationInfo = this._getLocationInfo(c);

                const supportedFormatDatasetTypes = c.dataType === 'Binary' ? c.propertyDefinition.excludedFormatDatasetTypes != null ?
                    formatTypeDatasetTypes.filter((datasetType) =>
                        c.propertyDefinition.excludedFormatDatasetTypes.indexOf(datasetType as formatTypeDatasetType) === -1) : formatTypeDatasetTypes : [];
                const copySourceTypes = this.getCopySourceSinkTypes(c, supportedFormatDatasetTypes);
                const copySinkTypes = this.getCopySourceSinkTypes(c, supportedFormatDatasetTypes, true);

                const storeReadSettingsType = this.getStoreSettingsTypes(c);
                const storeWriteSettingsType = this.getStoreSettingsTypes(c, true);
                const dataflowInlineSource: IDataflowTypeMetadataSource = this._getDataflowInfo(c.propertyDefinition.dataflowInlineSource, c.connectorName, 'dataflowInlineSource');
                const dataflowInlineSink = this._getDataflowInfo(c.propertyDefinition.dataflowInlineSink, c.connectorName, 'dataflowInlineSink');
                if (dataflowInlineSource) {
                    dataflowInlineSource.dataflowKind = dataKind;
                }

                // register ls first
                if (c.propertyDefinition.linkedService != null) {
                    const lsProperties = this._getAllDynamicPayloadProperties(c.propertyDefinition.linkedService.typeProperties) || [];
                    linkedSerivceMetaDatas.push({
                        type: c.propertyDefinition.linkedService.type,
                        linkedServiceNamePrefix: replaceSpecialChars(c.propertyDefinition.linkedService.type),
                        isFirstClass: false,
                        isDynamicallyGenerated: true,
                        linkedServiceKind: dataKind,
                        isOnPremises: !!c.onPremises,
                        displayName: c.displayName,
                        queryAlias: queryInfo && queryInfo.queryAlias,
                        copySourceTypes,
                        copySinkTypes,
                        supportDatasetV2: true,
                        supportFirstClassParameterExperience: true,
                        notSupportCopySource: copySourceTypes == null || copySourceTypes.length === 0,
                        notSupportCopySink: copySinkTypes == null || copySinkTypes.length === 0,
                        category: c.category || [],
                        icon: c.icon ?? getIconMetadata(c.propertyDefinition.linkedService.type, 'LinkedService').icon,
                        tableNameAlias: tableNameInfo && tableNameInfo.tableNameAlias,
                        controls: c.propertyDefinition.linkedService.typeProperties != null
                            ? this._convertPropertiesToControls(c.propertyDefinition.linkedService.typeProperties, c.connectorName, 'linkedService', c.supportEncryption) : undefined,
                        properties: lsProperties,
                        storeReadSettingsType,
                        // only register the below property whe dataset is null
                        storeReadSettingControls: c.propertyDefinition.dataset == null && c.propertyDefinition.storeReadSettings != null ?
                            this._convertPropertiesToControls(c.propertyDefinition.storeReadSettings.typeProperties, c.connectorName) : undefined,
                        storeReadSettingProperties: c.propertyDefinition.dataset == null && c.propertyDefinition.storeReadSettings != null ?
                            this._getAllDynamicPayloadProperties(c.propertyDefinition.storeReadSettings.typeProperties) : [],
                        storeWriteSettingsType,
                        storeWriteSettingControls: c.propertyDefinition.dataset == null && c.propertyDefinition.storeWriteSettings != null ?
                            this._convertPropertiesToControls(c.propertyDefinition.storeWriteSettings.typeProperties, c.connectorName) : undefined,
                        storeWriteSettingProperties: c.propertyDefinition.dataset == null && c.propertyDefinition.storeWriteSettings != null ?
                            this._getAllDynamicPayloadProperties(c.propertyDefinition.storeWriteSettings.typeProperties) : [],
                        locationType: locationInfo?.locationType,
                        locationProperties: locationInfo?.locationProperties,
                        locationPattern: locationInfo?.locationPattern,
                        fileNameAlias: locationInfo?.fileNameAlias,
                        isNonFilePathBasedLocation: locationInfo?.isNonFilePathBasedLocation,
                        nonFilePathBasedLocationControls: locationInfo?.nonFilePathBasedLocationControls,
                        locationDisplayName: locationInfo?.locationDisplayName,
                        sensitiveFieldsInConnectionString: this._getFieldsInfoInConnectionString(lsProperties)[0] || [],
                        allKnownFieldsInConnectionString: this._getFieldsInfoInConnectionString(lsProperties)[1] || [],
                        // dataflow
                        supportDataFlowSource: Boolean(dataflowInlineSource),
                        supportDataFlowSink: Boolean(dataflowInlineSink),
                        dataflowSource: dataflowInlineSource ? [dataflowInlineSource] : undefined,
                        dataflowSink: dataflowInlineSink ? [dataflowInlineSink] : undefined
                    });
                }

                // if contains dataset, register dataset
                if (!onlyHandleLinkedservice && c.propertyDefinition.dataset != null) {
                    datasetMetadDatas.push({
                        type: c.propertyDefinition.dataset.type,
                        displayName: c.displayName,
                        isFirstClass: false,
                        isDynamicallyGenerated: true,
                        datasetKind: dataKind,
                        supportLinkedServiceTypes: [c.propertyDefinition.linkedService.type],
                        isSource: copySourceTypes != null && copySourceTypes.length > 0,
                        isSink: copySinkTypes != null && copySinkTypes.length > 0,
                        tableNameAlias: tableNameInfo?.tableNameAlias,
                        schemaAlias: tableNameInfo?.schemaAlias,
                        tableAlias: tableNameInfo?.tableAlias,
                        tableNameDisplay: tableNameInfo?.tableNameDisplay,
                        splitTablePath: tableNameInfo?.splitTablePath,
                        isTableNameRequired: tableNameInfo?.isTableNameRequired,
                        disableTableName: tableNameInfo?.isTableNameDisabled,
                        supportedCopySourceTypes: copySourceTypes,
                        supportedCopySinkTypes: copySinkTypes,
                        category: c.category || [],
                        supportRootFolder: !!c.supportRootFolder,
                        supportCreatedAsV2: true, // all new dynamic connector supports to be created as v2 dataset
                        notSupportTypeConversion: c.dataType === 'Hierarchical', // type conversion is supported for only tabular source
                        notSupportCopySourceQuery: queryInfo && queryInfo.notSupportCopySourceQuery,
                        queryAlias: queryInfo && queryInfo.queryAlias,
                        queryDisplayName: (queryInfo && queryInfo.queryDisplayName) || stringResources.QueryLabel,
                        isQueryRequired: queryInfo && !!queryInfo.isQueryRequired,
                        supportGetMetadataActivity: c.activitySupportability != null && c.activitySupportability.supportGetMetadataActivity,
                        getMetadataActivityRequiredProperties: c.activitySupportability != null && c.activitySupportability.supportGetMetadataActivity ?
                            c.activitySupportability.getMetadataActivityRequiredProperties : [],
                        notSupportLookupActivity: !(c.activitySupportability != null && c.activitySupportability.supportLookupActivity),
                        lookupActivityRequiredProperties: c.activitySupportability != null && c.activitySupportability.supportLookupActivity ?
                            c.activitySupportability.lookupActivityRequiredProperties : [],
                        supportDeleteActivity: c.activitySupportability != null && c.activitySupportability.supportDeleteActivity,
                        deleteActivityRequiredProperties: c.activitySupportability != null && c.activitySupportability.supportDeleteActivity ?
                            c.activitySupportability.deleteActivityRequiredProperties : [],
                        ...getIconMetadata(c.propertyDefinition.linkedService.type, 'Dataset'),
                        controls: c.propertyDefinition.dataset.typeProperties != null ? this._convertPropertiesToControls(c.propertyDefinition.dataset.typeProperties, c.connectorName, 'dataset') : undefined,
                        properties: this._getAllDynamicPayloadProperties(c.propertyDefinition.dataset.typeProperties) || [],
                        copySourceControls: c.propertyDefinition.copySource != null && c.propertyDefinition.copySource.typeProperties != null
                            ? this._convertPropertiesToControls(c.propertyDefinition.copySource.typeProperties, c.connectorName, 'copySource') : undefined,
                        copySourceProperties: c.propertyDefinition.copySource != null && c.propertyDefinition.copySource.typeProperties != null
                            ? this._getAllDynamicPayloadProperties(c.propertyDefinition.copySource.typeProperties) : [],
                        copySinkControls: c.propertyDefinition.copySink != null && c.propertyDefinition.copySink.typeProperties != null
                            ? this._convertPropertiesToControls(c.propertyDefinition.copySink.typeProperties, c.connectorName, 'copySink') : undefined,
                        copySinkProperties: c.propertyDefinition.copySink != null && c.propertyDefinition.copySink.typeProperties != null
                            ? this._getAllDynamicPayloadProperties(c.propertyDefinition.copySink.typeProperties) : [],
                        copySourceRequiredProperties: c.propertyDefinition.copySource != null && c.propertyDefinition.copySource.requiredDatasetProperties != null
                            ? c.propertyDefinition.copySource.requiredDatasetProperties : [],
                        copySinkRequiredProperties: c.propertyDefinition.copySink != null && c.propertyDefinition.copySink.requiredDatasetProperties != null
                            ? c.propertyDefinition.copySink.requiredDatasetProperties : []
                    });
                } else if (!onlyHandleLinkedservice && !arrayIsNullOrEmpty(supportedFormatDatasetTypes)) {
                    // this connector supports static format type dataset, let's generate an alias dataset for it
                    // and include it into static format type dataset's supportedLinkedserviceTypes
                    supportedFormatDatasetTypes.forEach((formatDatasetType) => {
                        // we only take static format dataset type into consideration
                        if (isStaticFormatDataset(formatDatasetType)) {
                            datasetMetadDatas.push({
                                type: formatDatasetType,
                                displayName: c.displayName,
                                isDynamicallyGenerated: true,
                                ...getIconMetadata(c.propertyDefinition.linkedService.type, 'Dataset'),
                                isAlias: true,
                                datasetKind: 'unknown',
                                supportLinkedServiceTypes: [],
                                isSource: copySourceTypes && copySourceTypes.length > 0,
                                isSink: copySinkTypes && copySinkTypes.length > 0,
                                category: c.category,
                                linkedServiceType: c.propertyDefinition.linkedService.type
                            });
                        }
                    });
                }
            } catch (error) {
                // one configuration error should not impact other configurations
                const errorMessage = format(stringResources.ConnectorMetadata_LogMessageTemplate, `Invalid configuration: ${c.connectorName} ${convertObjectToString(error, false)}`);
                this._loggerService.error(DynamicConnectorService.className, errorMessage);
                return;
            }
        });

        // make sure first-class connectors are registered well
        await lastValueFrom(this._linkedServiceDynamicLoaderService.isInitialized$.pipe(filter((val) => val), take(1)));
        await lastValueFrom(this._dataflowFormatDynamicLoaderService.isInitialized$.pipe(filter((val) => val), take(1)));
        await lastValueFrom(this._datasetDynamicLoaderService.isInitialized$.pipe(filter((val) => val), take(1)));
        this._linkedServiceDynamicLoaderService.loadMetadatas(linkedSerivceMetaDatas);
        this._dataflowFormatDynamicLoaderService.loadAllInlineMetadataFromLinkedService();
        if (!onlyHandleLinkedservice) {
            this._datasetDynamicLoaderService.loadMetadatas(datasetMetadDatas);
        }
    }

    /**
     * Validate whether plugin metadata is in the correct format
     * connectorName is required
     * dataType can only be 'Binary', 'Tabular' or 'Hierarchical'
     * dataset, copySource and copySink can be set only when pluginMetadata.dataType === 'Tabular'or 'Hierarchical'
     * location, storeReadSettings and storeWriteSettings properties can be set only when pluginMetadata.dataType === 'Binary'
     */
    public validatePluginMetadataResult(pluginMetadata: IPluginMetadataSucceededResult): string {
        if (pluginMetadata == null) {
            return format(stringResources.ConnectorMetadata_ConfigRequiredTemplate, 'plugin metadata', '');
        }
        if (stringIsNullOrEmpty(pluginMetadata.connectorName)) {
            return format(stringResources.ConnectorMetadata_ConfigRequiredTemplate, 'connectorName', '');
        }
        if (pluginMetadata.dataType !== 'Binary' && pluginMetadata.dataType !== 'Tabular' && pluginMetadata.dataType !== 'Hierarchical') {
            return format(stringResources.ConnectorMetadata_InvalidConfigTemplate, 'dataType', pluginMetadata.connectorName);
        }
        if (arrayIsNullOrEmpty(pluginMetadata.connectionSettings)) {
            return format(stringResources.ConnectorMetadata_ConfigRequiredTemplate, 'connectionSettings', pluginMetadata.connectorName);
        }
        if (pluginMetadata.adfAppModelConfiguration == null) {
            return format(stringResources.ConnectorMetadata_ConfigRequiredTemplate, 'adfAppModelConfiguration', pluginMetadata.connectorName);
        }
        if (pluginMetadata.adfAppModelConfiguration.dataset != null || pluginMetadata.adfAppModelConfiguration.copySource != null || pluginMetadata.adfAppModelConfiguration.copySink != null) {
            if (pluginMetadata.dataType !== 'Tabular' && pluginMetadata.dataType !== 'Hierarchical') {
                return format(stringResources.ConnectorMetadata_InvalidConfigTemplate, 'dataset, copySource or copySink', pluginMetadata.connectorName);
            }
        }
        if (pluginMetadata.adfAppModelConfiguration.location != null || pluginMetadata.adfAppModelConfiguration.storeReadSettings != null ||
            pluginMetadata.adfAppModelConfiguration.storeWriteSettings != null) {
            if (pluginMetadata.dataType !== 'Binary') {
                return format(stringResources.ConnectorMetadata_InvalidConfigTemplate, 'location, storeReadSettings or storeWriteSettings', pluginMetadata.connectorName);
            }
        }
        const validateSettingItem = (settingItem: IPluginSettingsProperty, concept: BackendModelConceptType) => {
            if (settingItem == null) {
                return format(stringResources.ConnectorMetadata_ConfigRequiredTemplate, `Setting item in ${concept} cannont be null.`);
            }
            if (stringIsNullOrEmpty(settingItem.name) || stringIsNullOrEmpty(settingItem.type)) {
                return format(stringResources.ConnectorMetadata_InvalidConfigTemplate,
                    `Type or name in ${concept} item`, pluginMetadata.connectorName) + ` name: ${settingItem.name}, type: ${settingItem.type}.`;
            }
            return undefined;
        };
        if (pluginMetadata.connectionSettings != null) {
            const connectionSettingsResult = pluginMetadata.connectionSettings.map((item) => validateSettingItem(item, 'connectionSettings')).filter((value) => value);
            if (!arrayIsNullOrEmpty(connectionSettingsResult)) {
                return connectionSettingsResult.join(' ');
            }
        }
        if (pluginMetadata.readSettings != null) {
            const readSettingsResult = pluginMetadata.readSettings.map((item) => validateSettingItem(item, 'readSettings')).filter((value) => value);
            if (!arrayIsNullOrEmpty(readSettingsResult)) {
                return readSettingsResult.join(' ');
            }
        }
        if (pluginMetadata.writeSettings != null) {
            const writeSettingsResult = pluginMetadata.writeSettings.map((item) => validateSettingItem(item, 'writeSettings')).filter((value) => value);
            if (!arrayIsNullOrEmpty(writeSettingsResult)) {
                return writeSettingsResult.join(' ');
            }
        }
        return undefined;
    }

    private getCopySourceSinkTypes(config: IConnectorConfiguration, supportedFormatDatasetTypes: string[], isSink: boolean = false): string[] {
        if (!isSink && config.supportCopySource) {
            const copySourceTypes = config.dataType === 'Binary' && config.propertyDefinition && config.propertyDefinition.storeReadSettings != null ?
                getSupportedFormatCopySourceSinkType(supportedFormatDatasetTypes) :
                config.propertyDefinition.copySource != null ? [config.propertyDefinition.copySource.type] : [];
            return copySourceTypes;
        } else if (isSink && config.supportCopySink) {
            const copySinkTypes = config.dataType === 'Binary' && config.propertyDefinition && config.propertyDefinition.storeWriteSettings != null ?
                getSupportedFormatCopySourceSinkType(supportedFormatDatasetTypes, true) :
                config.propertyDefinition.copySink != null ? [config.propertyDefinition.copySink.type] : [];
            return copySinkTypes;
        }
        return [];
    }

    private getStoreSettingsTypes(config: IConnectorConfiguration, isStoreWriteSettings: boolean = false): string {
        if (config.dataType !== 'Binary') {
            return undefined;
        }
        if (!isStoreWriteSettings && config.supportCopySource) {
            return config.propertyDefinition.storeReadSettings != null ? config.propertyDefinition.storeReadSettings.type : undefined;
        } else if (isStoreWriteSettings && config.supportCopySink) {
            return config.propertyDefinition.storeWriteSettings != null ? config.propertyDefinition.storeWriteSettings.type : undefined;
        }
        return undefined;
    }

    // for properties in StaticTabularControl, they have been registered by inheriting from TabularDataset and TabularCopySource
    private _getAllDynamicPayloadProperties(properties: IConnectorProperty[], modelConcept?: UIModelConceptType): IConnectorProperty[] {
        const _getProperties = (_properties: IConnectorProperty[], _modelConcept?: UIModelConceptType): IConnectorProperty[] => {
            let allProperties = [..._properties.filter((property) => property.propertyName && !property.propertyName.startsWith('_') && !this._isStaticTabularControl(property.controlType))];
            _properties.forEach((property) => {
                if (!this._isStaticTabularControl(property.controlType, _modelConcept) && property.properties != null) {
                    allProperties = allProperties.concat(_getProperties(property.properties));
                }
            });
            return allProperties;
        };
        const propertyNames = new Set<string>();
        return _getProperties(properties, modelConcept).filter((property) => {
            if (!propertyNames.has(property.propertyName)) {
                propertyNames.add(property.propertyName);
                return true;
            }
            return false;
        });
    }

    // [sensitiveFields, allKnownFields]
    private _getFieldsInfoInConnectionString(lsProperties: IConnectorProperty[]): [string[], string[]] {
        if (arrayIsNullOrEmpty(lsProperties)) {
            return [undefined, undefined];
        }
        const connectionStringProperties = lsProperties.filter((p) => p.controlType === 'ConnectionStringContainer');
        const sensitiveFieldsResult = connectionStringProperties.map((p) => p.additionalProperties ?
            p.additionalProperties.sensitiveFieldsInConnectionString : []).reduce((acc, cur) => acc.concat(cur), []);
        const knownFieldsResult = connectionStringProperties.map((p) => p.additionalProperties ?
            p.additionalProperties.allKnownFieldsInConnectionString : []).reduce((acc, cur) => acc.concat(cur), []);
        return [sensitiveFieldsResult, knownFieldsResult];
    }

    private _isStaticTabularControl(controlType: UIControlType, modelConcept?: UIModelConceptType): boolean {
        if (controlType === 'SchemaTableSelection' && isDataflowConcept(modelConcept)) {
            return false;
        }
        return staticTabularConnectorControlTypes.includes(controlType);
    }

    private _isStaticLocationControl(controlType: UIControlType, modelConcept?: UIModelConceptType): boolean {
        return staticFileTypeConnectorControlTypes.includes(controlType);
    }

    private _convertPropertiesToControls(properties: IConnectorProperty[], connectorName: string, modelConcept?: UIModelConceptType, supportEncryption?: boolean): DynamicControlMetadata[] {
        if (properties == null) {
            return [];
        }
        // static controls won't be included in the result
        const dynamicProperties = properties.filter((p) => p && !this._isStaticTabularControl(p.controlType, modelConcept) && !this._isStaticLocationControl(p.controlType, modelConcept));
        const controlArray: DynamicControlMetadata[] = dynamicProperties.map((p) => {
            const bind: string = p.propertyName.startsWith('_') ? undefined : p.propertyName;
            const id = p.propertyName;
            const label = p.displayName || p.propertyName;
            const required = p.isRequired;
            const additionalProperties = p.additionalProperties != null ? p.additionalProperties : {};
            if (required) {
                if (p.validators == null) {
                    p.validators = [{
                        type: 'required'
                    }];
                } else {
                    p.validators.push({ type: 'required' });
                }
            }
            const requiredValidators = p.validators != null ? p.validators.filter((v) => v.type === 'required') : [];
            if (requiredValidators != null) {
                requiredValidators.forEach((v) => v.errorMessage = '');
            }

            // linked service credental properties
            if (p.isSensitive && modelConcept === 'linkedService' && p.controlType !== 'ConnectionStringContainer') {
                return {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    type: 'dynamic-akv-switcher' as any,
                    label,
                    bind,
                    id,
                    description: p.description,
                    isSubControl: false,
                    enableAKVParameterization: true,
                    akvOnly: !supportEncryption,
                    child: {
                        type: 'password',
                        label,
                        required,
                        isLabelVertical: modelConcept === 'linkedService',
                        validators: p.validators
                    }
                };
            }

            // azure resource
            if (p.controlType === 'AzureResourceSelection' && p.properties != null && p.properties.length === 2) {
                // handle azure select
                const manualInputProperties: IConnectorProperty[] = [];
                manualInputProperties.push(p.properties[0]);
                manualInputProperties.push(p.properties[1]);
                const manualCase: ISwitchCase<string> = {
                    caseValue: 'manual',
                    children: this._convertPropertiesToControls(manualInputProperties, connectorName, modelConcept, supportEncryption)
                };

                const azureCase: ISwitchCase<string> = {
                    caseValue: 'azure',
                    children: [{
                        type: 'azure-resource-select',
                        bind: {
                            resourceName: p.properties[0].propertyName,
                            resourceKey: p.properties[1].propertyName
                        },

                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        ...additionalProperties as any
                    }]
                };

                return {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    type: 'radio-switch' as any,
                    nested: 1,
                    label: stringResources.AccountSelectionMethodLabel,
                    bind,
                    id,
                    isSubControl: false,
                    options: azureResourceSelectMethodOptions,
                    isLabelVertical: true,
                    cases: [manualCase, azureCase],
                    defaultValue: 'azure'
                };
            }

            if (p.controlType === 'SchemaTableSelection' && isDataflowConcept(modelConcept)) {
                const schema = p.properties?.[0];
                const table = p.properties?.[1];
                let selectionType: IDataflowSchemaTableSelectionMetadata['selectionType'];
                let bind: IDataflowSchemaTableSelectionMetadata['bind'];
                if (!stringIsNullOrEmpty(schema?.propertyName) && !stringIsNullOrEmpty(table?.propertyName)) {
                    bind = {
                        schemaName: schema.propertyName,
                        tableName: table.propertyName
                    };
                    selectionType = ['schema', 'table'];
                } else if (!stringIsNullOrEmpty(schema?.propertyName)) {
                    selectionType = 'schema';
                    bind = schema.propertyName;
                } else if (!stringIsNullOrEmpty(table?.propertyName)) {
                    selectionType = 'table';
                    bind = table.propertyName;
                }
                const metadata: IDataflowSchemaTableSelectionMetadata = {
                    type: 'dataflow-schema-table-selection',
                    selectionType,
                    schemaNameLabel: schema?.displayName,
                    schemaNamePlaceholder: schema?.placeholder ?? schema?.displayName ? format(stringResources.DataFlowSchemaTableSelectionSchemaPlaceholder, schema.displayName) : undefined,
                    schemaRequired: Boolean(schema?.isRequired),
                    tableNameLabel: table?.displayName,
                    tableNamePlaceholder: table?.placeholder ?? table?.displayName ? format(stringResources.DataFlowSchemaTableSelectionSchemaPlaceholder, table.displayName) : undefined,
                    tableRequired: Boolean(table?.isRequired),
                    bind
                };
                if (p.additionalProperties?.dataflowSchemaTableSelectionBrowseSettings) {
                    metadata.browseSettings = p.additionalProperties.dataflowSchemaTableSelectionBrowseSettings;
                }

                const combinedSchemaTableSelectionProperties: IConnectorProperty[] = p.properties?.slice(2).filter((property) => !stringIsNullOrEmpty(property.propertyName));
                if (combinedSchemaTableSelectionProperties.length) {
                    metadata.combinedSchemaTableControls = this._convertPropertiesToControls(combinedSchemaTableSelectionProperties, connectorName, modelConcept);
                }
                return metadata;
            }

            // ArrayContainer, ConnectionStringContainer
            let children;
            let child;
            if (p.controlType === 'ConnectionStringContainer' && p.properties != null) {
                children = this._convertPropertiesToControls(p.properties, connectorName, modelConcept, supportEncryption);
            } else if (p.controlType === 'ArrayContainer' && p.item != null) {
                p.item.propertyName = 'itemPropertyNamePlaceholder';
                // do not parameterize inner array item as it's a fake property for UI rendering only
                p.item.isParameterizable = false;
                child = {
                    type: 'array-item',
                    children: this._convertPropertiesToControls([p.item], connectorName, modelConcept, supportEncryption)
                };
            }

            const control = {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                type: this._mapDynamicControlTypeToUIControlType(p.controlType) as any,
                label,
                id,
                isSubControl: false,
                required,
                isLabelVertical: modelConcept === 'linkedService',
                bind,
                defaultValue: p.defaultValue,
                placeholder: p.placeholder,
                isParameterizable: p.isParameterizable,
                description: p.description,
                dataType: p.dataType,
                validators: p.validators,
                children,
                child,
                escapeDescription: this._connectorTypeMap.get(connectorName) !== ConnectorType.INTERNAL,
                isDataFlowExpression: isDataflowConcept(modelConcept),
                ...additionalProperties
            };

            if (p.pattern) {
                const validator: IPatternMetadata = {
                    type: 'pattern',
                    pattern: p.pattern
                };
                if (control.validators) {
                    control.validators.push(validator);
                } else {
                    control.validators = [validator];
                }
            }

            if (p.isParameterizable) {
                return {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    type: 'parameterizable' as any,
                    child: control,
                    id,
                    isSubControl: false,
                    defaultValue: p.defaultValue,
                    placeholder: p.placeholder,
                    description: p.description,
                    dynamicContentTextClass: control.isLabelVertical ? undefined : 'input-spaced',
                    isDataFlowExpression: isDataflowConcept(modelConcept)
                };
            }
            return control;
        });

        // handle properties grouping
        dynamicProperties
            .filter((p) => p.additionalProperties != null && p.additionalProperties.options != null
                && p.additionalProperties.options.some((v) => !arrayIsNullOrEmpty(v.siblingProperties)))
            .forEach((p) => {
                const wrappedControl = controlArray.find((c) => c.id === p.propertyName);
                const control = wrappedControl && wrappedControl.type === 'parameterizable' ? wrappedControl.child : wrappedControl;
                if (control) {
                    control.type = control.type === 'radio' ? 'radio-switch' : 'select-switch';
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const selectSwitchCase: Array<ISwitchCase<any>> = p.additionalProperties.options.map((option) => ({
                        caseValue: option.value,
                        children: []
                    }));
                    p.additionalProperties.options.filter((v) => !arrayIsNullOrEmpty(v.siblingProperties)).forEach((optionWithSiblingProperties) => {
                        const controlsOfSibilingProperty = controlArray.filter((c) => optionWithSiblingProperties.siblingProperties.find((p) => p.toLowerCase() === c.id.toLowerCase()) != null);
                        const switchCaseOfTheOption = selectSwitchCase.find((c) => c.caseValue === optionWithSiblingProperties.value);
                        if (switchCaseOfTheOption != null && controlsOfSibilingProperty != null) {
                            switchCaseOfTheOption.children = switchCaseOfTheOption.children.concat(controlsOfSibilingProperty);
                        }
                        controlsOfSibilingProperty.forEach((c) => { c.isSubControl = true; });
                    });
                    (control as ISelectSwitchMetadata).cases = selectSwitchCase;
                    (control as ISelectSwitchMetadata).returnAllChildrenProperties = true;
                }
            });

        const finalResult: DynamicControlMetadata[] = controlArray.map((item) => item.type === 'parameterizable' && item.child &&
            (item.child.type === 'radio-switch' || item.child.type === 'select-switch') ? item.child : item).filter((c) => !c.isSubControl);
        return finalResult;
    }

    private _mapDynamicControlTypeToUIControlType(dynamicControlType: UIControlType): string {
        switch (dynamicControlType) {
            case 'TextBox':
                return 'textbox';
            case 'CheckBox':
                return 'checkbox';
            case 'TextBoxNumber':
                return 'textbox-number';
            case 'TextArea':
                return 'textarea';
            case 'MultiSelect':
                return 'multi-select';
            case 'DropDown':
                return 'select';
            case 'RadioButton':
                return 'radio';
            case 'DateTimePicker':
                return 'date-time-picker';
            case 'ObjectContainer':
                return 'complex-object-container';
            case 'ArrayContainer':
                return 'array-container';
            case 'TableSelection':
                return 'table-selection';
            case 'SchemaTableSelection':
                return 'schema-table-selection';
            case 'QueryTextArea':
                return 'textarea';
            case 'AzureResourceSelection':
                return 'azure-resource-select';
            case 'ConnectionStringContainer':
                return 'connection-string-container';
            case 'ManagementIdentityInfo':
                return 'management-identity-info';
            default: {
                return 'textbox';
            }
        }
    }

    // all linkedservice controls do not support parameter
    private _isControlTypeParameterizable(controlType: UIControlType): boolean {
        return !stringIsNullOrEmpty(controlType) && advancedControlTypes.indexOf(controlType) === -1;
    }

    // get existing ui control item of a property and its peer properties in the same control
    private _getExistingUIControlItem(settingProperty: IPluginSettingsProperty, uiControlItems: IUIControlsItem[]): [IUIControlsItem, string[]] {
        if (uiControlItems) {
            const getBindPropertyNames = (control: IUIControlsItem) => {
                const bindPropertyKeys: string[] = [];
                if (typeof control.bind !== 'string') {
                    Object.keys(control.bind).forEach((key) => {
                        const bindKey = (control.bind as { [key: string]: string })[key];
                        if (bindKey == null || typeof bindKey !== 'string') {
                            throw new Error(`Invalid bind key in UI control ${control.displayName}.`);
                        }
                        bindPropertyKeys.push((control.bind as { [key: string]: string })[key]);
                    });
                }
                return bindPropertyKeys;
            };
            const propertyGroupingMap: Map<string, string[]> = new Map();
            const uiControlItemForProperty = uiControlItems.filter((control) => {
                if (control.bind != null) {
                    // control.bind startWith('_') means this control is only for UI display, and won't bind to any model property
                    if (typeof control.bind === 'string' && !control.bind.startsWith('_')) {
                        if (control.bind === settingProperty.name) {
                            propertyGroupingMap.set(settingProperty.name, []);
                            return true;
                        }
                        return false;
                    } else {
                        const bindPropertyKeys = getBindPropertyNames(control);
                        if (bindPropertyKeys && bindPropertyKeys.includes(settingProperty.name)) {
                            propertyGroupingMap.set(settingProperty.name, bindPropertyKeys);
                            return true;
                        }
                        return false;
                    }
                } else if (control.controlType === 'FileLocation' && control.controlSettings != null && control.controlSettings.properties != null && control.controlSettings.properties.length > 0) {
                    const propertyNames: string[] = control.controlSettings.properties.map((p) => p.bind as string);
                    if (propertyNames.indexOf(settingProperty.name) !== -1) {
                        propertyGroupingMap.set(settingProperty.name, propertyNames);
                        // update required in control settings
                        const certainProperty = control.controlSettings.properties.find((p) => p.bind === settingProperty.name);
                        certainProperty.required = certainProperty.required != null ? certainProperty.required : settingProperty.required;
                        certainProperty.defaultValue = certainProperty.defaultValue !== undefined ? certainProperty.defaultValue : settingProperty.defaultValue;
                        return true;
                    }
                    return false;
                }
                return false;
            });
            if (uiControlItemForProperty.length > 1) {
                throw new Error(`Cannot bind one single property ${settingProperty.name} to several UI controls: ${uiControlItemForProperty.map((control) => control.displayName).join('/')}.`);
            }
            if (uiControlItemForProperty.length === 1) {
                return [uiControlItemForProperty[0], propertyGroupingMap.get(settingProperty.name)];
            }
        }
        return [undefined, undefined];
    }

    private _generateUIControlsForAllProperties(settingProperties: IPluginSettingsProperty[], uiControlItems: IUIControlsItem[]): IUIControlsItem[] {
        const newUIControlItems: IUIControlsItem[] = [];
        const tranversedProperties: Set<string> = new Set<string>();
        settingProperties.forEach((settingItem) => {
            if (tranversedProperties.has(settingItem.name)) {
                return;
            }
            tranversedProperties.add(settingItem.name);
            let uiControlItem;
            const uiControlItemForProperty = this._getExistingUIControlItem(settingItem, uiControlItems);
            if (uiControlItemForProperty[0] != null) {
                uiControlItem = uiControlItemForProperty[0];
            }
            // cache all bound properties, won't generate UI control for other bound properties in the following iterations
            if (uiControlItemForProperty[1] != null) {
                uiControlItemForProperty[1].forEach(tranversedProperties.add, tranversedProperties);
            }
            if (uiControlItem) {
                uiControlItem.dataType = settingItem.type;
                uiControlItem.displayName = uiControlItem.displayName || settingItem.displayName || settingItem.name;
                uiControlItem.required = uiControlItem.required != null ? uiControlItem.required : settingItem.required;
                uiControlItem.defaultValue = uiControlItem.defaultValue !== undefined ? uiControlItem.defaultValue : settingItem.defaultValue;
                if (settingItem.pattern) {
                    if (uiControlItem.controlSettings) {
                        uiControlItem.controlSettings.pattern = settingItem.pattern;
                    } else {
                        uiControlItem.controlSettings = { pattern: settingItem.pattern };
                    }
                }
            } else {
                // generate default ui control type
                uiControlItem = this._generateDefaultUIControlsForSettingProperty(settingItem);
            }
            newUIControlItems.push(uiControlItem);
        });
        // for UI controls which bind to a property starts with _, this is ony for UI display, the property will not be in the UI model
        uiControlItems.forEach((control) => {
            if (typeof control.bind === 'string' && control.bind.startsWith('_')) {
                newUIControlItems.push(control);
            }
        });
        return newUIControlItems;
    }

    private _generateConnectorPropertyFromUIControls(uiControlItems: IUIControlsItem[], sensitiveFields?: string[], appModelType?: UIModelConceptType, pluginMetadataResult?: IPluginMetadataSucceededResult): IConnectorProperty[] {
        const connectorName = pluginMetadataResult?.connectorName;
        const result: IConnectorProperty[] = [];
        uiControlItems.forEach((uiControlItem) => {
            if (!uiControlItem) {
                return;
            }
            const dynamicDataType = mapBackendDatatypeToDynamicType(uiControlItem.dataType);
            const isSensitive = sensitiveFields && sensitiveFields.indexOf(uiControlItem.bind as string) !== -1;
            const resultItem: IConnectorProperty = {
                propertyName: typeof uiControlItem.bind === 'string' ? uiControlItem.bind : undefined,
                controlType: uiControlItem.controlType || this._generateDefaultControlTypeFromDynamicDatatype(dynamicDataType),
                dataType: dynamicDataType,
                isSensitive,
                isRequired: uiControlItem.required,
                displayName: uiControlItem.displayName,
                defaultValue: uiControlItem.defaultValue,
                placeholder: uiControlItem.placeholder,
                description: uiControlItem.description,
                isParameterizable: !isSensitive && this._isControlTypeParameterizable(uiControlItem.controlType),
                pattern: uiControlItem.controlSettings?.pattern,
                validators: uiControlItem.validators,
                resourceName: typeof uiControlItem.bind === 'string' ? this._pluginMetadataProcessService.getResourceName(connectorName, uiControlItem.bind) : undefined
            };
            switch (resultItem.controlType) {
                case 'TextBox': {
                    if (typeof uiControlItem.bind !== 'string' || stringIsNullOrEmpty(uiControlItem.bind)) {
                        throw new Error(`Bind property in control ${uiControlItem.displayName} is invalid or null.`);
                    }
                    if (dynamicDataType === 'Float' || dynamicDataType === 'Int') {
                        resultItem.controlType = 'TextBoxNumber';
                        resultItem.additionalProperties = {
                            inputType: dynamicDataType === 'Float' ? 'float' : 'integer'
                        };
                    }
                    break;
                }
                case 'CheckBox': {
                    if (typeof uiControlItem.bind !== 'string' || stringIsNullOrEmpty(uiControlItem.bind)) {
                        throw new Error(`Bind property in control ${uiControlItem.displayName} is invalid or null.`);
                    }
                    if (dynamicDataType !== 'Boolean') {
                        throw new Error(`Bind property type in control ${uiControlItem.displayName} is invalid. Allowed type is 'Boolean'.`);
                    }
                    break;
                }
                case 'TextArea':
                case 'TextBoxNumber': {
                    if (typeof uiControlItem.bind !== 'string' || stringIsNullOrEmpty(uiControlItem.bind)) {
                        throw new Error(`Bind property in control ${uiControlItem.displayName} is invalid or null.`);
                    }
                    break;
                }
                case 'DropDown':
                case 'MultiSelect':
                case 'RadioButton': {
                    if (typeof uiControlItem.bind !== 'string' || stringIsNullOrEmpty(uiControlItem.bind)) {
                        throw new Error(`Bind property in control ${uiControlItem.displayName} is invalid or null.`);
                    }
                    if (uiControlItem.controlSettings == null || uiControlItem.controlSettings.options == null) {
                        throw new Error(`RadioButton error for ${uiControlItem.bind}, options are required.`);
                    }
                    const finalOptions = uiControlItem.controlSettings.options.map((opt) => ({ ...opt, label: opt.label || opt.value, value: opt.value }));
                    resultItem.additionalProperties = {
                        options: finalOptions
                    };
                    break;
                }
                case 'ArrayContainer': {
                    if (typeof uiControlItem.bind !== 'string' || stringIsNullOrEmpty(uiControlItem.bind)) {
                        throw new Error(`Bind property in control ${uiControlItem.displayName} is invalid or null.`);
                    }
                    if (uiControlItem.controlSettings == null || uiControlItem.controlSettings.itemUiControl == null) {
                        uiControlItem.controlSettings = {
                            itemUiControl: { bind: 'itemPropertyNamePlaceholder' }
                        };
                    }
                    uiControlItem.controlSettings.itemUiControl.bind = 'itemPropertyNamePlaceholder';
                    resultItem.item = this._generateConnectorPropertyFromUIControls([uiControlItem.controlSettings.itemUiControl], [], appModelType, pluginMetadataResult)[0];
                    break;
                }
                case 'AzureResourceSelection': {
                    resultItem.propertyName = '_azureResource'; // starts with _, this proprty bind oesn't correspond to a model property.
                    resultItem.dataType = 'AzureResource';
                    /* eslint-disable */
                    const resourceNameProperty = (uiControlItem.bind as { [type: string]: string })['resourceName'];
                    const resourceKeyProperty = (uiControlItem.bind as { [type: string]: string })['resourceKey'];
                    if (stringIsNullOrEmpty(resourceNameProperty)) {
                        throw new Error(`resourceNameProperty is required in AzureResourceSelection for ${uiControlItem.displayName}.`);
                    }
                    if (stringIsNullOrEmpty(resourceKeyProperty)) {
                        throw new Error(`resourceKeyProperty is required in AzureResourceSelection for ${uiControlItem.displayName}.`);
                    }
                    resultItem.properties = [{
                        dataType: 'String',
                        isRequired: true,
                        propertyName: resourceNameProperty
                    }, {
                        dataType: 'String',
                        isRequired: true,
                        propertyName: resourceKeyProperty,
                        isSensitive: true
                    }];
                    resultItem.additionalProperties = uiControlItem.controlSettings != null ? {
                        resourceNameLabel: uiControlItem.controlSettings.resourceDisplayName || resourceNameProperty,
                        resourceTypes: uiControlItem.controlSettings.resourceTypes,
                        resourceDisplayName: uiControlItem.controlSettings.resourceDisplayName,
                        resourceConvertTemplate: uiControlItem.controlSettings.resourceConvertTemplate,
                        supportListKey: uiControlItem.controlSettings.supportListKey,
                        listKeyAction: uiControlItem.controlSettings.listKeyAction,
                        supportSubresource: uiControlItem.controlSettings.supportSubresource,
                        subresourceApiVersion: uiControlItem.controlSettings.subresourceApiVersion,
                        subresourceCommand: uiControlItem.controlSettings.subresourceCommand,
                        listKeyApiVersion: uiControlItem.controlSettings.listKeyApiVersion,
                        keyResponseKeywords: uiControlItem.controlSettings.keyResponseKeywords
                    } : {};
                    break;
                }
                case 'ConnectionStringContainer': {
                    resultItem.dataType = 'ConnectionString';
                    if (uiControlItem.controlSettings == null) {
                        throw new Error(`controlSettings is required in ConnectionString ${uiControlItem.displayName}.`);
                    }
                    if (arrayIsNullOrEmpty(uiControlItem.controlSettings.properties)) {
                        throw new Error(`properties cannot be null or empty in ConnectionStringContainer control for ${uiControlItem.displayName}.`);
                    }
                    let connectionStringSensitiveFields: string[] = [];
                    if (!arrayIsNullOrEmpty(uiControlItem.controlSettings.sensitiveProperties)) {
                        connectionStringSensitiveFields = uiControlItem.controlSettings.sensitiveProperties;
                    }
                    resultItem.properties = this._generateConnectorPropertyFromUIControls(uiControlItem.controlSettings.properties, connectionStringSensitiveFields, appModelType, pluginMetadataResult);
                    const parameterBindings: { [name: string]: string } = {};
                    const allKnownFieldsInConnectionString: string[] = [];
                    if (resultItem.properties != null) {
                        this._getAllDynamicPayloadProperties(resultItem.properties).forEach((subp) => {
                            parameterBindings[subp.propertyName] = subp.propertyName;
                            allKnownFieldsInConnectionString.push(subp.propertyName);
                        });
                    }
                    resultItem.additionalProperties = {
                        combineRule: uiControlItem.controlSettings.combineRule,
                        parameterBindings,
                        sensitiveFieldsInConnectionString: connectionStringSensitiveFields,
                        allKnownFieldsInConnectionString
                    };
                    break;
                }
                case 'SchemaTableSelection': {
                    resultItem.propertyName = '_schemaTable'; // starts with _, this property bind doesn't correspond to a model property.
                    resultItem.dataType = 'SchemaTable';
                    const schemaProperty = (uiControlItem.bind as { [type: string]: string })['schema'];
                    const tableProperty = (uiControlItem.bind as { [type: string]: string })['table'];
                    if (!isDataflowConcept(appModelType)) {
                        if (stringIsNullOrEmpty(schemaProperty)) {
                            throw new Error(`schemaProperty is required in SchemaTableSelection control for ${uiControlItem.displayName}.`);
                        }
                        if (stringIsNullOrEmpty(tableProperty)) {
                            throw new Error(`tableProperty is required in SchemaTableSelection control for ${uiControlItem.displayName}.`);
                        }
                    }
                    let schema;
                    let table;
                    if (appModelType === 'dataflowInlineSource') {
                        schema = pluginMetadataResult.readSettings?.find((setting) => setting.name === schemaProperty);
                        table = pluginMetadataResult.readSettings?.find((setting) => setting.name === tableProperty);
                    } else if (appModelType === 'dataflowInlineSink') {
                        schema = pluginMetadataResult.writeSettings?.find((setting) => setting.name === schemaProperty);
                        table = pluginMetadataResult.writeSettings?.find((setting) => setting.name === tableProperty);
                    }
                    resultItem.properties = [{
                        dataType: 'String',
                        controlType: 'TextBox',
                        propertyName: schemaProperty,
                        displayName: schema?.displayName ?? schemaProperty,
                        isRequired: schema?.required,
                        resourceName: this._pluginMetadataProcessService.getResourceName(connectorName, schemaProperty)
                    }, {
                        dataType: 'String',
                        controlType: 'TextBox',
                        propertyName: tableProperty,
                        displayName: table?.displayName ?? tableProperty,
                        isRequired: table?.required,
                        resourceName: this._pluginMetadataProcessService.getResourceName(connectorName, tableProperty)
                    }];
                    if (uiControlItem.controlSettings?.properties) {
                        resultItem.properties.push(...this._generateConnectorPropertyFromUIControls(uiControlItem.controlSettings.properties, [], appModelType, pluginMetadataResult));
                    }
                    if (uiControlItem.controlSettings?.browseSettings) {
                        resultItem.additionalProperties = {
                            dataflowSchemaTableSelectionBrowseSettings: uiControlItem.controlSettings?.browseSettings
                        };
                    }
                    break;
                }
                case 'FileLocation': {
                    if (uiControlItem.controlSettings == null) {
                        throw new Error(`'controlSettings' is required in FileLocation control for ${uiControlItem.displayName}.`);
                    }
                    const locationProperties = uiControlItem.controlSettings.properties;
                    const fileNameProperty = uiControlItem.controlSettings.fileNameProperty;
                    if (arrayIsNullOrEmpty(locationProperties)) {
                        throw new Error(`'properties' cannot be null or empty in FileLocation control 'controlSettings' ${uiControlItem.displayName}.`);
                    }
                    if (locationProperties.length === 1) {
                        locationProperties[0].controlSettings = {
                            pattern: '*'
                        };
                    } else if (locationProperties.length === 2
                        && (!locationProperties[0].controlSettings || stringIsNullOrEmpty(locationProperties[0].controlSettings.pattern))
                        && (!locationProperties[1].controlSettings || stringIsNullOrEmpty(locationProperties[1].controlSettings.pattern))) {
                        locationProperties[0].controlSettings = {
                            pattern: '([^]*)/[^/]*'
                        };
                        locationProperties[1].controlSettings = {
                            pattern: '(?:[^]*/)?([^/]*)'
                        };
                    } else if (locationProperties.length === 3
                        && (!locationProperties[0].controlSettings || stringIsNullOrEmpty(locationProperties[0].controlSettings.pattern))
                        && (!locationProperties[1].controlSettings || stringIsNullOrEmpty(locationProperties[1].controlSettings.pattern))
                        && (!locationProperties[2].controlSettings || stringIsNullOrEmpty(locationProperties[2].controlSettings.pattern))) {
                        locationProperties[0].controlSettings = {
                            pattern: '([^/]*)/?[^]*'
                        };
                        locationProperties[1].controlSettings = {
                            pattern: '[^/]*/([^]*)/[^/]*'
                        };
                        locationProperties[2].controlSettings = {
                            pattern: '[^]*/([^/]*)'
                        };
                    } else if (locationProperties.length > 3 && locationProperties.some((p) => !p.controlSettings || stringIsNullOrEmpty(p.controlSettings.pattern))) {
                        throw new Error(`Location pattern cannot be null when location properties number are more than 3.`);
                    }
                    resultItem.propertyName = '_filePathLocation'; // starts with _, this proprty bind doesn't correspond to a model property.
                    resultItem.dataType = 'FilePath';
                    const locationSettingProperties: IConnectorProperty[] = [];
                    locationProperties.forEach((p) => {
                        const locationPropertySetting: IConnectorProperty = {
                            dataType: 'String',
                            propertyName: p.bind as string,
                            controlType: 'TextBox',
                            displayName: p.displayName,
                            description: p.description,
                            placeholder: p.placeholder,
                            defaultValue: p.defaultValue,
                            pattern: p.controlSettings.pattern,
                            isRequired: p.required,
                            resourceName: this._pluginMetadataProcessService.getResourceName(connectorName, p.bind as string)
                        };
                        // handle file name property
                        if ((p.bind as string) === fileNameProperty) {
                            locationPropertySetting.isFileNameProperty = true;
                        }
                        if (locationPropertySetting.isRequired) {
                            locationPropertySetting.validators = [{
                                type: 'required'
                            }];
                        }
                        locationSettingProperties.push(locationPropertySetting);
                    });
                    resultItem.properties = locationSettingProperties;
                    break;
                }
                default: {
                    if (uiControlItem.controlSettings != null) {
                        resultItem.additionalProperties = { ...uiControlItem.controlSettings };
                    }
                    break;
                }
            }
            result.push(resultItem);
        });
        return result;
    }

    private _generateDefaultUIControlsForSettingProperty(settingItem: IPluginSettingsProperty): IUIControlsItem {
        const result: IUIControlsItem = {
            bind: settingItem.name,
            dataType: settingItem.type,
            controlType: this._generateDefaultControlTypeFromDynamicDatatype(mapBackendDatatypeToDynamicType(settingItem.type)),
            displayName: settingItem.name,
            required: settingItem.required,
            defaultValue: settingItem.defaultValue
        };
        return result;
    }

    private _generateDefaultControlTypeFromDynamicDatatype(dataType: DynamicDataType): UIControlType {
        switch (dataType) {
            case 'String': {
                return 'TextBox';
            }
            case 'Int':
            case 'Float': {
                return 'TextBoxNumber';
            }
            case 'DateTime': {
                return 'DateTimePicker';
            }
            case 'Object': {
                return 'ObjectContainer';
            }
            default: {
                return 'TextBox';
            }
        }
    }

    private _getPropertyInPropertySettings(settings: IPluginSettingsProperty[], propertyName: string): IPluginSettingsProperty {
        return settings.find((p) => (p.name === propertyName));
    }

    private _generateUIModelProperties(pluginMetadataResult: IPluginMetadataSucceededResult, propertyNames: string[], appModelType: UIModelConceptType): IPluginSettingsProperty[] {
        if (arrayIsNullOrEmpty(propertyNames)) {
            return [];
        }
        const resultProperties: IPluginSettingsProperty[] = [];
        propertyNames.forEach((propertyName) => {
            let settingItem;
            let backendConnectorConcept: BackendModelConceptType;
            if (appModelType === 'linkedService') {
                backendConnectorConcept = 'connectionSettings';
                // lookup from connection settings
                if (pluginMetadataResult.connectionSettings != null) {
                    const itemInConnectionSettings = this._getPropertyInPropertySettings(pluginMetadataResult.connectionSettings, propertyName);
                    if (itemInConnectionSettings != null) {
                        settingItem = itemInConnectionSettings;
                    }
                }
            } else if (appModelType === 'copySource' || appModelType === 'storeReadSettings' || appModelType === 'dataflowInlineSource') {
                backendConnectorConcept = 'readSettings';
                // lookup from read settings
                if (pluginMetadataResult.readSettings != null) {
                    const itemInReadSettings = this._getPropertyInPropertySettings(pluginMetadataResult.readSettings, propertyName);
                    if (itemInReadSettings != null) {
                        settingItem = itemInReadSettings;
                    }
                }
            } else if (appModelType === 'copySink' || appModelType === 'storeWriteSettings' || appModelType === 'dataflowInlineSink') {
                backendConnectorConcept = 'writeSettings';
                // lookup from write settings
                if (pluginMetadataResult.writeSettings != null) {
                    const itemInWriteSettings = this._getPropertyInPropertySettings(pluginMetadataResult.writeSettings, propertyName);
                    if (itemInWriteSettings != null) {
                        settingItem = itemInWriteSettings;
                    }
                }
            } else if (appModelType === 'location' || appModelType === 'dataset') {
                backendConnectorConcept = 'readSettings or writeSettings';
                // look up from both read settings and write settings
                if (pluginMetadataResult.readSettings != null) {
                    const itemInReadSettings = this._getPropertyInPropertySettings(pluginMetadataResult.readSettings, propertyName);
                    if (itemInReadSettings != null) {
                        settingItem = itemInReadSettings;
                    }
                }
                if (settingItem == null && pluginMetadataResult.writeSettings != null) {
                    const itemInWriteSettings = this._getPropertyInPropertySettings(pluginMetadataResult.writeSettings, propertyName);
                    if (itemInWriteSettings != null) {
                        settingItem = itemInWriteSettings;
                    }
                }
            }
            if (settingItem == null) {
                throw new Error(format(stringResources.ConnectorMetadata_InvalidConfigTemplate, `${appModelType} properties ${propertyName}`, pluginMetadataResult.connectorName)
                    + ' ' + format(stringResources.ConnectorMetadata_CannotFindPropertyConfigTemplate, backendConnectorConcept));
            }
            resultProperties.push(settingItem);
        });
        return resultProperties;
    }

    private _getPropertySettingsOfModelConcept(pluginMetadataResult: IPluginMetadataSucceededResult, onlyHandleLinkedservice?: boolean): IPropertySettingsOfModelConcept {
        const result: IPropertySettingsOfModelConcept = {
            linkedService: pluginMetadataResult.connectionSettings
        };
        const linkedServiceProperties = pluginMetadataResult.adfAppModelConfiguration?.linkedService?.properties ?? [];
        const dataflowInlineSourceProperties = pluginMetadataResult.adfAppModelConfiguration?.dataflowInlineSource?.properties ?? [];
        const dataflowInlineSinkProperties = pluginMetadataResult.adfAppModelConfiguration?.dataflowInlineSink?.properties ?? [];

        result.linkedService = arrayIsNullOrEmpty(linkedServiceProperties) ? pluginMetadataResult.connectionSettings :
            this._generateUIModelProperties(pluginMetadataResult, linkedServiceProperties, 'linkedService');
        result.dataflowInlineSource = arrayIsNullOrEmpty(dataflowInlineSourceProperties) ? [] : this._generateUIModelProperties(pluginMetadataResult, dataflowInlineSourceProperties, 'dataflowInlineSource');
        result.dataflowInlineSink = arrayIsNullOrEmpty(dataflowInlineSinkProperties) ? [] : this._generateUIModelProperties(pluginMetadataResult, dataflowInlineSinkProperties, 'dataflowInlineSink');

        if (!onlyHandleLinkedservice) {
            const datasetProperties = pluginMetadataResult.adfAppModelConfiguration?.dataset?.properties ?? [];
            const copySourceProperties = pluginMetadataResult.adfAppModelConfiguration?.copySource?.properties ?? [];
            const copySinkProperties = pluginMetadataResult.adfAppModelConfiguration?.copySink?.properties ?? [];
            const locationProperties = pluginMetadataResult.adfAppModelConfiguration?.location?.properties ?? [];
            const storeReadSettingsProperties = pluginMetadataResult.adfAppModelConfiguration?.storeReadSettings?.properties ?? [];
            const storeWriteSettingsProperties = pluginMetadataResult.adfAppModelConfiguration?.storeWriteSettings?.properties ?? [];

            if (pluginMetadataResult.dataType === 'Binary') {
                result.location = arrayIsNullOrEmpty(locationProperties) ? [] : this._generateUIModelProperties(pluginMetadataResult, locationProperties, 'location');
                result.storeReadSettings = arrayIsNullOrEmpty(storeReadSettingsProperties) ? [] :
                    this._generateUIModelProperties(pluginMetadataResult, storeReadSettingsProperties, 'storeReadSettings');
                result.storeWriteSettings = arrayIsNullOrEmpty(storeWriteSettingsProperties) ? [] :
                    this._generateUIModelProperties(pluginMetadataResult, storeWriteSettingsProperties, 'storeWriteSettings');
            } else if (pluginMetadataResult.dataType === 'Tabular' || pluginMetadataResult.dataType === 'Hierarchical') {
                result.dataset = arrayIsNullOrEmpty(datasetProperties) ? [] : this._generateUIModelProperties(pluginMetadataResult, datasetProperties, 'dataset');
                result.copySource = arrayIsNullOrEmpty(copySourceProperties) ? [] : this._generateUIModelProperties(pluginMetadataResult, copySourceProperties, 'copySource');
                result.copySink = arrayIsNullOrEmpty(copySinkProperties) ? [] : this._generateUIModelProperties(pluginMetadataResult, copySinkProperties, 'copySink');
            }
        }
        return result;
    }

    private _generateConnectorConfigsFromPluginConfigs(pluginMetadataResult: IPluginMetadataSucceededResult, propertySettingsOfModelConcept: IPropertySettingsOfModelConcept,
        appModelType: UIModelConceptType): IConnectorProperty[] {
        const result: IConnectorProperty[] = [];
        let sensitiveFields: string[] = [];
        if (appModelType === 'linkedService') {
            sensitiveFields = !arrayIsNullOrEmpty(pluginMetadataResult.sensitiveFields) ? pluginMetadataResult.sensitiveFields :
                pluginMetadataResult.connectionSettings.filter((setting) => setting && setting.sensitive).map((setting) => setting.name);
        }
        /* eslint-disable @typescript-eslint/no-explicit-any */
        const existingPluginPropertySettings: IPluginSettingsProperty[] = (propertySettingsOfModelConcept as any)[appModelType];
        const existingUISettings: IAdfAppModelConfigurationItem = (pluginMetadataResult.adfAppModelConfiguration as any)[appModelType];
        const existingUIControls: IUIControlsItem[] = existingUISettings != null ? existingUISettings.uiControls : [];
        const newUiControlItems = this._generateUIControlsForAllProperties(existingPluginPropertySettings || [], existingUIControls || []);
        result.push(...this._generateConnectorPropertyFromUIControls(newUiControlItems, sensitiveFields, appModelType, pluginMetadataResult));
        return result;
    }

    private _getLocationInfo(c: IConnectorConfiguration): ILocationInfo {
        if (c.propertyDefinition.dataset == null && c.propertyDefinition.location != null) {
            const locationType = c.propertyDefinition.location.type;
            const locationDetails = c.propertyDefinition.location.typeProperties ?
                c.propertyDefinition.location.typeProperties.filter((p) => p.controlType === 'FileLocation') : undefined;
            const locationProperties = this._getAllDynamicPayloadProperties(c.propertyDefinition.location.typeProperties) || [];
            const locationPattern: ILocationPattern = {};
            let fileNameAlias;
            let locationDisplayName: string;
            let isFilePathBasedLocation;
            if (locationDetails && locationDetails.length === 1 && locationDetails[0] && locationDetails[0].properties) {
                isFilePathBasedLocation = true;
                locationDetails[0].properties.forEach((property) => {
                    locationPattern[property.propertyName] = property.pattern;
                    if (property.isFileNameProperty) {
                        fileNameAlias = property.propertyName;
                    }
                });
                locationDisplayName = locationDetails[0].displayName;
            }
            const nonFilePathBasedLocationControls: ControlMetadata[] = !isFilePathBasedLocation && c.propertyDefinition.location.typeProperties ?
                this._convertPropertiesToControls(c.propertyDefinition.location.typeProperties, c.connectorName)
                : undefined;

            return {
                locationType,
                isNonFilePathBasedLocation: !isFilePathBasedLocation,
                nonFilePathBasedLocationControls,
                locationProperties,
                locationPattern,
                fileNameAlias,
                locationDisplayName
            };
        }
        return {};
    }

    private _getDataflowInfo<T extends 'dataflowInlineSource' | 'dataflowInlineSink'>(
        dataflowConfiguration: IConnectorConfiguration['propertyDefinition'][T],
        connectorName: string,
        modelConcept: T
    ): IDataflowTypeMetadata {
        if (dataflowConfiguration == undefined) {
            return undefined;
        }
        const metadata: IDataflowTypeMetadata = {
            type: dataflowConfiguration.type,
            format: dataflowConfiguration.format,
            settings: `${dataflowConfiguration.type}Settings`,
            properties: this._getAllDynamicPayloadProperties(dataflowConfiguration.typeProperties, modelConcept),
            controls: this._convertPropertiesToControls(dataflowConfiguration.typeProperties, connectorName, modelConcept)
        };
        if (modelConcept === 'dataflowInlineSource') {
            const dataflowSourceConfiguration = dataflowConfiguration as IConnectorConfiguration['propertyDefinition']['dataflowInlineSource'];
            const sourceMetadata = metadata as IDataflowTypeMetadataSource;
            sourceMetadata.useInlineProjection = dataflowSourceConfiguration.useInlineProjection;
            sourceMetadata.connectTransformationProperties = dataflowSourceConfiguration.connectTransformationProperties;
        }
        return metadata;
    }
}

// below are functions only used this file.
function convertDataStructureToDataKind(dataStructure: 'Binary' | 'Tabular' | 'Hierarchical'):
    'file' | 'table' | 'hierarchical' | 'unknown' {
    switch (dataStructure) {
        case 'Binary':
            return 'file';
        case 'Tabular':
            return 'table';
        case 'Hierarchical':
            return 'hierarchical';
        default:
            return 'unknown';
    }
}

function getIconMetadata(linkedServiceType: string, concept: 'Dataset' | 'LinkedService'): IIconRegistery {
    if (dynamicConnectorIconRegistery[linkedServiceType] != null) {
        return dynamicConnectorIconRegistery[linkedServiceType];
    }
    return dynamicConnectorIconRegistery[concept];
}

function getTableNameInfo(datasetProperties: IConnectorProperty[]): ISchemaTableInfo {
    if (datasetProperties) {
        const tableProperty = datasetProperties.filter((property) => property && (property.controlType === 'TableSelection' || property.controlType === 'SchemaTableSelection'));
        if (tableProperty.length === 0) {
            return {
                isTableNameDisabled: true
            };
        }
        const resultProperty = tableProperty[0];
        if (resultProperty != null) {
            const isTableNameRequired = tableProperty[0].isRequired || (tableProperty[0].validators && tableProperty[0].validators.filter((validator) => validator.type === 'required').length > 0);
            if (resultProperty.controlType === 'TableSelection') {
                return {
                    isTableNameDisabled: false,
                    tableNameAlias: resultProperty.propertyName,
                    isTableNameRequired,
                    tableNameDisplay: resultProperty.displayName
                };
            } else if (resultProperty.controlType === 'SchemaTableSelection') {
                const schemaTableProperties = resultProperty.properties;
                const schemaAlias = schemaTableProperties && schemaTableProperties.length > 0 ? schemaTableProperties[0].propertyName : undefined;
                const tableAlias = schemaTableProperties && schemaTableProperties.length > 1 ? schemaTableProperties[1].propertyName : undefined;
                return {
                    isTableNameDisabled: false,
                    schemaAlias,
                    tableAlias,
                    splitTablePath: true,
                    tableNameDisplay: resultProperty.displayName
                };
            }
        }
    }
    return {
        isTableNameDisabled: true
    };
}

function getQueryInfo(copySourceProperties: IConnectorProperty[]): IQueryInfo {
    if (copySourceProperties) {
        const queryProperty = copySourceProperties.filter((property) => property && property.controlType === 'QueryTextArea');
        if (queryProperty.length === 0) {
            return {
                notSupportCopySourceQuery: true
            };
        }
        return {
            notSupportCopySourceQuery: false,
            queryAlias: queryProperty[0].propertyName,
            queryDisplayName: queryProperty[0].displayName,
            isQueryRequired: queryProperty[0].isRequired
        };
    }
    return {
        notSupportCopySourceQuery: true
    };
}

function isDataflowConcept(modelConcept: UIModelConceptType): boolean {
    return modelConcept === 'dataflowInlineSource' || modelConcept === 'dataflowInlineSink';
}
